<template>
  <div
    v-if="(images?.length || videos?.length) && mediaItems?.length"
    class="h-full w-full"
  >
    <div class="relative h-full">
      <Swiper
        :modules="swiperModules"
        :pagination="{ clickable: true }"
        :navigation="false"
        :slides-per-view="1"
        :class="{ 'w-full': true, 'h-full': isScheduled }"
        @swiper="onSwiper"
        @slide-change="onSlideChange"
      >
        <SwiperSlide v-for="item in mediaItems" :key="item.url">
          <img
            v-if="item.type === 'image'"
            :src="enforceFilesDomain(item.url)"
            alt="post"
            class="h-64 w-full object-cover bg-slate-200 dark:bg-slate-800"
          />
          <video
            v-else-if="item.type === 'video'"
            :src="enforceFilesDomain(item.url)"
            controls
            class="h-64 w-full object-cover bg-slate-200 dark:bg-slate-800"
            playsinline
            preload="metadata"
          >
            Your browser does not support the video tag.
          </video>
        </SwiperSlide>
      </Swiper>

      <!-- Custom Navigation Buttons -->
      <div class="absolute top-1/2 left-4 z-10 transform -translate-y-1/2">
        <AppButton
          v-if="mediaItems.length > 1"
          icon="pi pi-arrow-left"
          :disabled="isBeginning"
          class="text-white!"
          rounded
          @click.stop="prev"
        />
      </div>

      <div class="absolute top-1/2 right-4 z-10 transform -translate-y-1/2">
        <AppButton
          v-if="mediaItems.length > 1"
          icon="pi pi-arrow-right"
          class="text-white!"
          :disabled="isEnd"
          rounded
          @click.stop="next"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Navigation, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const props = defineProps<{
  images?: string[];
  videos?: string[];
  isScheduled?: boolean;
}>();

interface MediaItem {
  url: string;
  type: "image" | "video";
}

const mediaItems = computed<MediaItem[]>(() => {
  const items: MediaItem[] = [];

  if (props.images?.length) {
    items.push(...props.images.map(url => ({ url, type: "image" as const })));
  }

  if (props.videos?.length) {
    items.push(...props.videos.map(url => ({ url, type: "video" as const })));
  }

  return items;
});

const swiperModules = [Navigation, Pagination];
const swiper = ref<SwiperType | null>(null);
const isBeginning = ref(true);
const isEnd = ref(false);

const onSwiper = (swiperInstance: SwiperType) => {
  swiper.value = swiperInstance;
  updateNavigationState();
};

const onSlideChange = () => {
  updateNavigationState();
};

const updateNavigationState = () => {
  if (swiper.value) {
    isBeginning.value = swiper.value.isBeginning;
    isEnd.value = swiper.value.isEnd;
  }
};

const prev = () => {
  if (swiper.value) {
    swiper.value.slidePrev();
  }
};

const next = () => {
  if (swiper.value) {
    swiper.value.slideNext();
  }
};
</script>
